import { Component, computed, DestroyRef, inject, input, linkedSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ActionConfirmDirective } from '@app/+authenticated/shared/directives/action-confirm.directive';
import { DefaultPipe } from '@app/pipes/default.pipe';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { mgcIconAttachment2Regular } from '@sb/mgc-icons/mgcIcon-attachment_2_regular.icon';
import { mgcIconDelete2Regular } from '@sb/mgc-icons/mgcIcon-delete_2_regular.icon';
import { mgcIconDownload3Regular } from '@sb/mgc-icons/mgcIcon-download_3_regular.icon';
import { mgcIconPencilRegular } from '@sb/mgc-icons/mgcIcon-pencil_regular.icon';
import { SectionToolbarComponent } from '@sb/shared/layout/primitives';
import { TooltipDirective } from '@sb/tooltip';
import { AnchorButtonComponent, IconButtonComponent } from '@sb/ui/button';
import { CardComponent } from '@sb/ui/card';
import { EmptyStateComponent, LoadingStateComponent } from '@sb/ui/feedback';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { PAGINATION_DEFAULT_ITEMS_PER_PAGE, PaginationChangeEvent, PaginationComponent } from '@sb/ui/pagination';
import { SearchInputComponent } from '@sb/ui/search-input';
import {
  TableCellPrimitiveDirective,
  TableContainerDirective,
  TableDirective,
  TableHeaderCellDirective,
  TableRowDirective,
} from '@sb/ui/table';
import chunk from 'lodash-es/chunk';

import { UserFile } from '../../../../reducers/orm/employee/employee.model';
import { EmployeeService } from '../../../../reducers/orm/employee/employee.service';

export interface EmployeeFilePermissions {
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canReview: boolean;
}

@Component({
  selector: 'employee-files',
  templateUrl: './employee-files.component.html',
  imports: [
    RouterLink,
    TranslatePipe,
    LocaleDatePipe,
    DefaultPipe,
    AnchorButtonComponent,
    IconButtonComponent,
    IconComponent,
    PaginationComponent,
    CardComponent,
    EmptyStateComponent,
    LoadingStateComponent,
    TooltipDirective,
    ActionConfirmDirective,
    SectionToolbarComponent,
    SearchInputComponent,
    TableContainerDirective,
    TableDirective,
    TableRowDirective,
    TableCellPrimitiveDirective,
    TableHeaderCellDirective,
  ],
})
export class EmployeeFilesComponent {
  #employeeService = inject(EmployeeService);
  #destroyRef = inject(DestroyRef);

  employeeId = input<string>();
  files = input<UserFile[]>([]);
  loading = input(false);
  permissions = input.required<EmployeeFilePermissions>();

  searchQuery = signal('');

  itemsPerPage = PAGINATION_DEFAULT_ITEMS_PER_PAGE;

  // Reset to the first page whenever the search query changes; still writable for paging.
  page = linkedSignal({ source: this.searchQuery, computation: () => 1 });

  filteredFiles = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const files = this.files();

    return query ? files.filter((file) => file.title.toLowerCase().includes(query)) : files;
  });

  pages = computed(() => chunk(this.filteredFiles(), this.itemsPerPage));

  currentPageFiles = computed(() => this.pages()[this.page() - 1] ?? []);

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconAttachment2Regular,
      mgcIconDownload3Regular,
      mgcIconPencilRegular,
      mgcIconDelete2Regular,
    ]);
  }

  onPageChange(options: PaginationChangeEvent) {
    this.page.set(options.page);
  }

  download(file: UserFile) {
    this.#employeeService.download(file.id, file.file_name).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
  }

  deleteFile(file: UserFile) {
    const employeeId = this.employeeId();
    if (!employeeId) {
      return;
    }

    this.#employeeService.deleteFile(employeeId, file.id).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
  }
}
